import { useEffect, useState } from 'react';
import client from '../api/client';
import endpoints from '../api/endpoints';

/**
 * 선택한 dateTime 이 휴무일에 해당하는지 판별.
 * BugHerd #67 후속(2026-05-09): 페이지 첫 로드 시 dateTime default(오늘) 가 휴무일이면
 * 모달이 자동으로 떠버려 사용자 경험 망침. → 자동 모달 표시 제거. hook 은 holidayInfo 만 제공.
 * 모달은 호출부(검색 버튼 onClick)에서 명시적으로 trigger.
 *
 * 반환:
 *   holidayInfo: { date, amount, label, blocked } | null  (자동 detect, 표시는 호출부 책임)
 *     blocked=true → 운행 불가 (결제 차단)
 *     blocked=false → 추가요금만 (안내성)
 */
export default function useHolidayWarning(dateTime) {
  const [holidays, setHolidays] = useState([]);
  const [holidayInfo, setHolidayInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    client.get(endpoints.common.holidays)
      .then((res) => { if (!cancelled) setHolidays(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { /* 조회 실패해도 BE 가 가격 계산 시 surcharge 자동 합산 */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!dateTime) { setHolidayInfo(null); return; }
    const m = String(dateTime).match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!m) { setHolidayInfo(null); return; }
    const ymd = `${m[1]}-${m[2]}-${m[3]}`;
    const pickupTs = new Date(dateTime).getTime();

    // BugHerd #74: 시간대 휴무 (blocked_window) 우선 검사 — pickup 시각이 윈도우 안에 들면 차단.
    if (!Number.isNaN(pickupTs)) {
      const window = holidays.find((h) => {
        if (!h.is_active || !h.start_at || !h.end_at) return false;
        const s = new Date(h.start_at).getTime();
        const e = new Date(h.end_at).getTime();
        return !Number.isNaN(s) && !Number.isNaN(e) && pickupTs >= s && pickupTs <= e;
      });
      if (window) {
        setHolidayInfo({
          date: ymd,
          amount: 0,
          label: `${window.start_at?.slice(0, 16).replace('T', ' ')} ~ ${window.end_at?.slice(0, 16).replace('T', ' ')}`,
          blocked: true,
        });
        return;
      }
    }

    // 기존 종일 휴무 (specific_date). 회귀 주의: start_at/end_at 가 둘 다 있는 row 는
    // "시간대 휴무" 이므로 specific_date 가 같아도 종일 차단 대상에서 제외. 윈도우 밖
    // 시각(예: 12일 18:00~13일 09:00 윈도우에 13일 19:00)이 정상 통과되어야 함.
    const hit = holidays.find((h) =>
      h.specific_date === ymd && h.is_active && !(h.start_at && h.end_at)
    );
    if (!hit) { setHolidayInfo(null); return; }
    const amount = Number(hit.surcharge_amount || 0);
    // 운영 정책(2026-05-27): surcharge_amount > 0 인 지정일은 "공휴일 추가요금"(예약 가능, 가산만),
    // 0/NULL 이면 "휴무"(예약 차단). BE(availability.py / reservations.py)와 동일 기준.
    // #191 추가: "공휴일 추가요금은 고지 없이 자동 계산" 클라 요청 — 차단 안 되는 할증일은
    // 안내 모달도 띄우지 않도록 holidayInfo 자체를 null 반환. 가격 가산은 BE 가 처리.
    if (amount > 0) { setHolidayInfo(null); return; }
    setHolidayInfo({
      date: ymd,
      amount,
      label: ymd,
      blocked: true,
    });
  }, [dateTime, holidays]);

  return { holidayInfo };
}
